/**
 * Typed fetch client for the portal API.
 *
 * The SPA is served from a different origin to the API (web.* and api.* behind the same
 * Cloudflare proxy), so every request sends credentials — session cookies are set on the
 * parent domain and are not attached without it.
 *
 * `import.meta.env` is the only environment there is in a browser: there is no
 * `process.env`. Everything reachable through it is compiled into the bundle and public,
 * so nothing secret is ever read here.
 */

import { contentDisposition } from '@portal/shared';

const RAW_BASE: string = import.meta.env.VITE_API_URL ?? '';
/** Trailing slash removed once, so path joining never produces `//api/issues`. */
const API_BASE = RAW_BASE.replace(/\/+$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string | undefined;

  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

/** A 404 is the API's uniform answer for "missing, or not yours". Never a 403. */
export class NotFoundError extends ApiError {
  constructor(message = 'Not found') {
    super(404, message);
    this.name = 'NotFoundError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'Sign in required') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

export const SIGN_IN_PATH = '/sign-in';

/**
 * Send the browser to sign-in, preserving where it was going.
 *
 * Guarded against repeat navigation: several queries can fail 401 together in one render
 * pass, and assigning `location.href` per failure stacks history entries the user then has
 * to click back through.
 */
let redirecting = false;
function redirectToSignIn(): void {
  if (typeof window === 'undefined' || redirecting) return;
  if (window.location.pathname === SIGN_IN_PATH) return;
  redirecting = true;
  const next = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`${SIGN_IN_PATH}?next=${encodeURIComponent(next)}`);
}

interface ApiErrorBody {
  error?: unknown;
  detail?: unknown;
}

async function readErrorBody(response: Response): Promise<{ error: string; detail?: string }> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    return {
      error: typeof body.error === 'string' ? body.error : response.statusText,
      ...(typeof body.detail === 'string' ? { detail: body.detail } : {}),
    };
  } catch {
    // A proxy error page or an empty body is an ordinary occurrence, not a bug worth
    // surfacing over the status the caller actually needs.
    return { error: response.statusText || `Request failed (${response.status})` };
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Query parameters. Entries that are undefined, null or '' are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
}

/**
 * An absolute URL on the API origin.
 *
 * Exported for the one case that must NAVIGATE the browser rather than fetch: resuming an OAuth
 * authorization after sign-in. Everything else goes through `apiRequest`, which builds its own
 * URL — reach for this only when a redirect, not a response, is the point.
 */
export function apiUrl(path: string): string {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Turn a failed response into the error the SPA renders, or return for a successful one.
 *
 * One function rather than three copies of the same three branches. The 404 arm is the reason
 * it matters: the API answers 404 uniformly for "missing" and "not yours", and a caller that
 * lost that distinction — an upload or a download that threw a plain `ApiError` — would render
 * a generic failure panel where every other screen renders the not-found state.
 */
async function throwForStatus(response: Response): Promise<void> {
  if (response.status === 401) {
    redirectToSignIn();
    throw new UnauthorizedError();
  }

  if (response.status === 404) {
    const { error } = await readErrorBody(response);
    throw new NotFoundError(error);
  }

  if (!response.ok) {
    const { error, detail } = await readErrorBody(response);
    throw new ApiError(response.status, error, detail);
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, query } = options;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      // Cross-subdomain session cookie. Without this the API sees an anonymous request.
      credentials: 'include',
      headers:
        body === undefined
          ? { Accept: 'application/json' }
          : {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(
      0,
      'Could not reach the server',
      error instanceof Error ? error.message : undefined,
    );
  }

  await throwForStatus(response);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** How an upload reports itself while it is happening. 0 to 1, called many times. */
export type UploadProgress = (fraction: number) => void;

/**
 * Upload one file, as the body of the request.
 *
 * XHR, NOT `fetch`, AND THAT IS THE WHOLE REASON THIS IS NOT `apiRequest`. `fetch` has no
 * upload progress event and never will without a duplex request stream, which is not available
 * in the browsers this application supports. At 10 MB that was tolerable; at 250 MB it is a
 * client watching a dead screen for several minutes with no way to tell a slow upload from a
 * hung one. The status this reports is the difference between waiting and giving up.
 *
 * THE BODY IS THE FILE. It used to be `multipart/form-data` with one part, which bought
 * nothing — there is one file and no other fields — and cost the ability to stream it on the
 * API side. The name travels in `Content-Disposition`, written by the same function the API
 * reads it with.
 *
 * The status mapping is `throwForStatus`, reached by rebuilding the answer as a `Response`, so
 * an upload refusal is exactly the error a JSON refusal would have been — including the 401
 * that redirects to sign-in. A second mapping here is how the two would come to disagree.
 */
export function apiUpload<T>(
  path: string,
  file: File,
  options: { onProgress?: UploadProgress; signal?: AbortSignal } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', buildUrl(path, undefined));
    // Cross-subdomain session cookie. Without this the API sees an anonymous request.
    request.withCredentials = true;
    request.setRequestHeader('Accept', 'application/json');
    request.setRequestHeader('Content-Disposition', contentDisposition(file.name));
    // Only when the platform recognised the file. An empty type is an ABSENT claim, which is
    // what lets the API's sniff decide alone; sending `''` would be a claim of nothing.
    if (file.type !== '') request.setRequestHeader('Content-Type', file.type);

    if (options.onProgress) {
      const report = options.onProgress;
      request.upload.onprogress = (event) => {
        // `lengthComputable` is false for a body of unknown size, which a File never is — but
        // the guard costs nothing and a NaN fraction would render as a broken percentage.
        if (event.lengthComputable && event.total > 0) report(event.loaded / event.total);
      };
    }

    request.onload = () => {
      void (async () => {
        try {
          const response = new Response(request.responseText, { status: request.status });
          await throwForStatus(response);
          resolve(
            request.status === 204 ? (undefined as T) : (JSON.parse(request.responseText) as T),
          );
        } catch (error) {
          reject(error);
        }
      })();
    };

    // The same wrapping `apiRequest` and `apiDownload` do. An upload is the request most likely
    // to lose its connection — it is the longest and the largest — and a bare event reaching
    // the caller means `attachmentFailure` cannot recognise it, so the client is shown a
    // browser diagnostic instead of a sentence.
    request.onerror = () => reject(new ApiError(0, 'Could not reach the server'));
    request.ontimeout = () => reject(new ApiError(0, 'Could not reach the server'));
    request.onabort = () => reject(new DOMException('The upload was cancelled', 'AbortError'));

    options.signal?.addEventListener('abort', () => request.abort(), { once: true });

    request.send(file);
  });
}

/**
 * Fetch bytes rather than JSON — today, an attachment's contents.
 *
 * Deliberately a `fetch` through this module rather than a bare `<a href>` or an `<img src>`
 * pointed at the API. A plain link would work: `web` and `api` share the registrable domain
 * and `COOKIE_DOMAIN` carries a leading dot, so `SameSite=Lax` attaches the session. What it
 * would not do is fail properly — a 404 for an id belonging to another project would land the
 * client on the browser's own error page instead of the not-found state every other screen
 * renders, and a 503 would look like the portal was broken rather than the bucket.
 *
 * The blob is the caller's to turn into an object URL, and the caller's to revoke: only the
 * component that renders it knows when it stops being on screen.
 */
export async function apiDownload(path: string): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path, undefined), {
      method: 'GET',
      credentials: 'include',
      // No `Accept: application/json` — the answer is a PNG or a PDF. An error is still JSON,
      // and `readErrorBody` parses that regardless of what was asked for.
      headers: { Accept: '*/*' },
    });
  } catch (error) {
    throw new ApiError(
      0,
      'Could not reach the server',
      error instanceof Error ? error.message : undefined,
    );
  }

  await throwForStatus(response);
  return await response.blob();
}

/** True when a query failure should render the "not found" state rather than an error. */
export function isNotFound(error: unknown): boolean {
  return error instanceof NotFoundError;
}

/** A 401 has already navigated away; there is nothing useful to render for it. */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof UnauthorizedError;
}

/**
 * Signed in, and a member of nothing.
 *
 * A 403 rather than a 401, deliberately — the API's `noActiveOrganization` — because the
 * session is perfectly valid and sending the browser back to sign-in would loop it. It is
 * what an invited person has until they accept, and what a removed member has afterwards, so
 * it needs a screen of its own rather than a generic failure panel showing a code.
 */
export function isNoActiveOrganization(error: unknown): boolean {
  return error instanceof ApiError && error.message === 'no_active_organization';
}

/**
 * The API's error CODES, said in words.
 *
 * `ApiError.message` holds the API's machine code — `rate_limited`, `no_active_organization` —
 * and `errorMessage` used to render it straight into `ErrorPanel`. So a client whose session
 * lookup was throttled read "Something went wrong" followed by the single word `rate_limited`,
 * which tells them nothing and reads like the product leaking its own plumbing at them.
 *
 * Only codes a person can actually meet are listed. `invalid_signature` and the webhook ones
 * belong to machine callers and never reach a browser; they fall through to the generic
 * sentence rather than being given wording nobody will read.
 */
const CODE_SENTENCES: Record<string, string> = {
  unauthenticated: 'Your session has expired. Sign in again to carry on.',
  no_active_organization: 'No client is selected for this session yet.',
  forbidden: 'You do not have access to that.',
  not_found: 'That is not here — it may have been removed, or the link may be wrong.',
  conflict: 'Somebody else changed this while you were working on it. Reload and try again.',
  rate_limited: 'That is more requests than we take in one go. Wait a moment and try again.',
  rate_limiter_unavailable:
    'We could not check the rate limit, so the request was refused rather than risk running it twice. Trying again shortly is safe.',
  github_not_configured: 'No issue tracker is connected to this deployment yet.',
  storage_not_configured: 'File storage is not switched on for this deployment.',
  attachment_too_large: 'That file is too large to attach.',
  internal_error: 'Something failed on our side. Nothing you did caused it.',
};

const NETWORK_SENTENCE = 'We could not reach the portal. Check your connection and try again.';

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // Status 0 is the browser's own verdict — offline, dropped, or refused at a CORS preflight —
    // so there is no code to look up, and the generic fallback hid that nothing arrived at all.
    if (error.status === 0) return NETWORK_SENTENCE;
    const sentence = CODE_SENTENCES[error.message];
    // `detail` is written for a person — "title: Required" — so it is kept where it exists.
    // The bare code never is, which is why there is no branch that returns it alone.
    if (sentence !== undefined) return error.detail ? `${sentence} (${error.detail})` : sentence;
    return error.detail ?? 'Something went wrong.';
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}
